import { baseUrl } from "util/APIBaseUrl";
import getRequest from "util/APIHelperGet";
import postRequest from "util/APIHelperPost";

export interface Group {
  Group_ID: string;
  Customer_ID: string;
  Parent_Group_ID: string | null;
  Name: string;
  Path: string;
  Is_Main: boolean;
}

const root = `${baseUrl}/v4/groups`;

export const getGroups = async (customerId?: string): Promise<Group[]> => {
  const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : "";
  const res = await getRequest(`${root}${qs}`, false);
  return res.ok ? (res.payload as Group[]) : [];
};

export const createGroup = (parentGroupId: string, name: string) =>
  postRequest(root, JSON.stringify({ parentGroupId, name }), true);

export const renameGroup = (groupId: string, name: string) =>
  postRequest(`${root}/${groupId}`, JSON.stringify({ name }), true, "PATCH");

export const deleteGroup = (groupId: string) =>
  postRequest(`${root}/${groupId}`, undefined, true, "DELETE");

export const moveGroup = (groupId: string, newParentId: string) =>
  postRequest(`${root}/${groupId}/move`, JSON.stringify({ newParentId }), true);

export const assignMember = (
  groupId: string,
  principalType: "employee" | "customerUser",
  principalId: string
) =>
  postRequest(
    `${root}/${groupId}/members`,
    JSON.stringify({ principalType, principalId }),
    true
  );

export interface AssignMemberInput {
  principalType: "employee" | "customerUser";
  principalId: string;
}

// Bulk assign: POST { members: [{ principalType, principalId }] } to the group's members endpoint.
export const assignMembers = (groupId: string, members: AssignMemberInput[]) =>
  postRequest(`${root}/${groupId}/members`, JSON.stringify({ members }), true);

export interface MemberOption {
  value: string; // principalId expected by assignMember (Customer_User.User_ID or Employee.Employee_ID)
  label: string;
  email?: string;
  currentGroupId?: string | null; // Group_ID the candidate currently belongs to, if any.
}

const fullName = (first?: string, last?: string) => `${first ?? ""} ${last ?? ""}`.trim();

// Customer users of a customer -> options whose value is the User_ID (the principalId for `customerUser`).
export const getCustomerUserOptions = async (customerId: string): Promise<MemberOption[]> => {
  const url = `${baseUrl}/v2/customer/${encodeURIComponent(customerId)}/customerUsers?limit=1000&page=0`;
  const res = await getRequest(url, false);
  const list = res.ok ? (res.payload?.list ?? []) : [];
  return list.map((u: any) => ({
    value: u.userId,
    label: fullName(u.firstName, u.lastName) || u.name || u.email || u.userId,
    email: u.email,
    currentGroupId: u.Group_ID ?? u.groupId ?? null,
  }));
};

// Talent assigned to a customer -> options whose value is the Employee_ID (the principalId for `employee`).
export const getEmployeeOptions = async (customerId: string): Promise<MemberOption[]> => {
  const url = `${baseUrl}/customer/${encodeURIComponent(customerId)}/employees/all`;
  const res = await getRequest(url, false);
  const list = Array.isArray(res?.payload) ? res.payload : [];
  return list.map((e: any) => ({
    value: e.employeeId,
    label: fullName(e.firstName, e.lastName) || e.email || e.employeeId,
    email: e.email,
    currentGroupId: e.Group_ID ?? e.groupId ?? null,
  }));
};
